import { styled, useTheme } from "@mui/material";
import MxIcons from "@rf-l1/MxIcons";
import MxTooltipErrorsViewer from "@rf-l1/tooltip/MxTooltipErrorsViewer";
import { IconAlertTriangleFilled } from "@tabler/icons-react";
import { memo } from "react";
import { useFormPropertySlice } from "../../store/slices/FormPropertySlice";

interface IconIndicatorProps {
  status: string;
  errorsStep: { label: string; value: string }[];
  disabledRedirect?: boolean;
}

/**
 * IconIndicator - Displays various status indicators based on form state
 *
 * Shows:
 * - Warning icon with tooltip when there are errors
 * - Success circle when form is valid and dirty
 * - Gray circle for new, untouched forms
 * - Optional loading state when validating
 */
const IconIndicator = (props: IconIndicatorProps) => {
  const { getCurrentStep, setStep } = useFormPropertySlice();
  const theme = useTheme();

  const goTo = (id: string, step: number) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  };

  const goToStep = (step: number) => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        if (getCurrentStep() !== step) {
          setStep(step);
        }
        resolve(true);
      }, 500);
    });
  };

  const goToField = async (state: {
    label: string;
    value: string;
    meta: { labelStep: string; idStep: number };
  }) => {
    await goToStep(state.meta.idStep);

    if (state.label.includes("Etapa")) {
      const [nameField, building, unit] = state.label.split(" ");
      goTo(`building-${building}-index`, 4);
    }
  };

  // Determine which indicator to show
  if (props.status === "loading") {
    return <Circle color={theme.palette.warning.main} className="circle-header" />;
  }

  if (props.status === "error") {
    return (
      <MxTooltipErrorsViewer
        sx={{ width: 400, height: "auto", maxHeight: 400, overflowY: "auto" }}
        errors={props.errorsStep}
        onClickAlert={
          props?.disabledRedirect ? undefined : state => goToField(state)
        }
      >
        <MxIcons color="warning.main" size={14}>
          <IconAlertTriangleFilled />
        </MxIcons>
      </MxTooltipErrorsViewer>
    );
  }

  if (props.status === "success") {
    return <Circle color={theme.palette.success.main} className="circle-header" />;
  }

  if (props.status === "info") {
    return <Circle color={theme.palette.grey[400]} className="circle-header" />;
  }

  return null;
};

// Memoize component with proper equality check
export default IconIndicator;

const Circle = styled("span")<{ color: string }>(props => ({
  width: "10px",
  height: "10px",
  borderRadius: "50%",
  backgroundColor: props.color,
  display: "inline-block"
}));
